import React from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export default function Phase6ModeSelection({ wizardData, setWizardData, onNext, onPrev }) {
  return (
    <div className="space-y-6">
      <div>
        <Label className="text-lg font-semibold mb-4 block">
          What are the likely best modes for your project?
        </Label>
        <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <p className="text-sm text-slate-700 dark:text-slate-300 mb-2">
            <strong>These modes are applicable based on procurement type.</strong> Ranking is based on fit-for-purpose approach considering:
          </p>
          <ul className="text-xs text-slate-600 dark:text-slate-400 space-y-1 ml-4">
            <li>• Nature and complexity of project</li>
            <li>• Market condition and readiness</li>
            <li>• Urgency or risks</li>
            <li>• Economy and efficiency and value for money</li>
          </ul>
        </div>
        
        {/* Risk Level Definition */}
        <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border-2 border-amber-400 dark:border-amber-600">
          <div className="flex items-start gap-2">
            <span className="text-xl">⚠️</span>
            <p className="text-sm text-amber-900 dark:text-amber-200 flex-1">
              <strong className="text-base">Risk Level:</strong> The expected overall risk (low, medium, or high) that is usually associated with using that particular mode, based on how complex it is and how likely problems (like delays, disputes, audit findings, or integrity issues) are to occur when that mode is used.
            </p>
          </div>
        </div>
        
        {/* Ranking Status */}
        {wizardData.rankingLoading && (
          <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg flex items-center gap-2">
            <svg className="animate-spin h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-sm text-blue-700 dark:text-blue-300">Ranking modes based on your assessment...</span>
          </div>
        )}
        
        <RadioGroup value={wizardData.mainMode} onValueChange={(value) => setWizardData({ ...wizardData, mainMode: value })}>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {wizardData.rankedModes.length > 0 ? (
              wizardData.rankedModes.map((rankedMode, index) => {
                let badgeColor = 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';
                let badgeText = `#${index + 1}`;
                
                if (index === 0) {
                  badgeColor = 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-white font-bold';
                  badgeText = '🥇 Top 1';
                } else if (index === 1) {
                  badgeColor = 'bg-gradient-to-r from-slate-300 to-slate-400 text-slate-900 font-bold';
                  badgeText = '🥈 Top 2';
                } else if (index === 2) {
                  badgeColor = 'bg-gradient-to-r from-amber-600 to-amber-700 text-white font-bold';
                  badgeText = '🥉 Top 3';
                }
                
                return (
                  <div key={rankedMode.id} className="border rounded-lg p-4 hover:border-emerald-500 transition-all relative">
                    <div className={`absolute top-2 right-2 px-3 py-1 rounded-full text-xs ${badgeColor}`}>
                      {badgeText}
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <RadioGroupItem value={rankedMode.id} id={`main-${rankedMode.id}`} className="mt-1" />
                      <Label htmlFor={`main-${rankedMode.id}`} className="cursor-pointer flex-1 pr-20">
                        <p className="font-medium mb-2">{rankedMode.name}</p>
                        <div className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                          <div className="flex items-center gap-3">
                            <span className="px-2 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 rounded text-xs font-semibold">
                              Score: {rankedMode.score}
                            </span>
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              rankedMode.riskLevel === 'Low' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                              rankedMode.riskLevel === 'Medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' :
                              rankedMode.riskLevel === 'Medium–High' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300' :
                              'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                            }`}>
                              Risk: {rankedMode.riskLevel}
                            </span>
                          </div>
                          
                          {rankedMode.scenario && rankedMode.scenario !== 'All goods and general support services, except CSEs not under centralized procurement' && (
                            <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded text-xs">
                              <span className="font-semibold text-blue-900 dark:text-blue-300">Scenario: </span>
                              <span>{rankedMode.scenario}</span>
                            </div>
                          )}
                        </div>
                      </Label>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="p-8 text-center text-slate-500">
                <p>Loading modes... Please wait.</p>
              </div>
            )}
          </div>
        </RadioGroup>
      </div>
      
      <div className="flex gap-3">
        <Button variant="outline" onClick={onPrev} size="lg">
          <ChevronLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <Button onClick={onNext} className="flex-1 bg-emerald-600 hover:bg-emerald-700" size="lg">
          Next <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
